import { DestroyRef, inject, Inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Inspection, InspectionResolutionStatus } from './inspection.dto';
import { WarehouseSyncEventService } from '../sync/warehouse-sync-event.service';
import { Issue } from '../issue/issue.dto';
import { AssetService } from '../asset/asset.service';
import { ChangeTyped, IndexedService, INSPECTIONS_STORE, Query, SearchResult } from '@damofire/data-commons';
import { AuthDtoSignerService } from '@damofire/data-security';
import { COMMONS_ENV, HttpService, ServiceEvent, ServiceEventType } from '@damofire/data-angular-commons';
import { IndexedQueryService } from '../indexed/indexed-query.service';
import { LockingIndexedService } from '../indexed/locking-indexed.service';
import { DocumentData } from '@angular/fire/firestore';
import { IssueService } from '../issue/issue.service';
import { InspectionService } from './inspection.service';
import { logger } from '@sentry/angular';

@Injectable()
export class IdbInspectionService extends InspectionService {
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly idbService: IndexedService,
    private readonly lockingIdbService: LockingIndexedService,
    private readonly warehouseSyncEventService: WarehouseSyncEventService,
    private readonly dtoSigner: AuthDtoSignerService,
    private readonly queryService: IndexedQueryService,
    protected readonly http: HttpService,
    protected readonly issueService: IssueService,
    protected readonly assetService: AssetService,
    @Inject(COMMONS_ENV) protected readonly environment: any,
  ) {
    super(http, issueService, environment, assetService);

    // To replace the missing Cloud Functions when offline
    this.issueService
      .events()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => this.updateInspectionMeta(event));
  }

  protected async dbCreate(inspection: Partial<Inspection>): Promise<Inspection> {
    await this.dtoSigner.sign(inspection, true);
    await this.idbService.add(INSPECTIONS_STORE, inspection);

    const changeTyped: ChangeTyped<Inspection> = { ...inspection, changeType: 'added' };
    this.warehouseSyncEventService.publishOutput({
      division: inspection.division._id,
      changes: [changeTyped],
      store: INSPECTIONS_STORE,
    });
    super.publishEvent(ServiceEventType.CREATED, inspection);
    return inspection;
  }

  async update(division: string, id: string, update: DocumentData | Inspection): Promise<void> {
    await this.flagCompletion(division, id, update);
    await this.dtoSigner.sign(update, false, false);
    logger.info('Updating inspection (idb)', { category: 'idb-inspection', id, division });
    this.idbService.update(INSPECTIONS_STORE, update).catch(console.error);
    const changes = [{ ...update, changeType: 'modified' } as ChangeTyped<Inspection>];
    this.warehouseSyncEventService.publishOutput({ division, changes, store: INSPECTIONS_STORE });
  }

  async delete(division: string, id: string): Promise<void> {
    await this.idbService.delete(INSPECTIONS_STORE, [division, id]).catch(console.error);
    this.warehouseSyncEventService.publishOutput({
      division,
      changes: [{ _id: id, changeType: 'removed' } as ChangeTyped<Inspection>],
      store: INSPECTIONS_STORE,
    });
  }

  async deleteBatch(division: string, inspectionIds: string[], deleteIssues = false): Promise<void> {
    await this.deleteAll(division, inspectionIds, deleteIssues);
  }

  async search(division: string, sQuery: Query): Promise<SearchResult<Inspection>> {
    return this.queryService.collection<Inspection>(INSPECTIONS_STORE, division, sQuery, ['name']);
  }

  async get(division: string, id: string): Promise<Inspection> {
    return this.lockingIdbService.getByKey(division, INSPECTIONS_STORE, [division, id]);
  }

  /**
   * The inspection is pushed before its issues, notes and pictures, so completing it here only flags
   * it: the server snapshots it once the sync has pushed everything and cleared the flag
   * (WarehousePushSyncCompletedSyncStepTask).
   */
  private async flagCompletion(division: string, id: string, update: DocumentData | Inspection): Promise<void> {
    if (update.status?.status !== InspectionResolutionStatus.RESOLVED || update.snapshotId) {
      return;
    }

    const stored = await this.idbService.getByKey<Inspection>(INSPECTIONS_STORE, [division, id]);
    if (stored?.status?.status !== InspectionResolutionStatus.RESOLVED) {
      update.awaitingSync = true;
    }
  }

  private async updateInspectionMeta(event: ServiceEvent<Issue>): Promise<void> {
    const inspectionMeta: { [id: string]: number } = {};
    let division: string;
    for (const changes of event.items) {
      const beforeInspectionId = changes.before?.inspection?._id;
      const afterInspectionId = changes.after?.inspection?._id;
      division = changes.before?.division._id || changes.after?.division._id;

      if (beforeInspectionId && beforeInspectionId !== afterInspectionId) {
        inspectionMeta[beforeInspectionId] = inspectionMeta[beforeInspectionId] - 1 || -1;
      }

      if (afterInspectionId && beforeInspectionId !== afterInspectionId) {
        inspectionMeta[afterInspectionId] = inspectionMeta[afterInspectionId] + 1 || 1;
      }
    }

    for (const id of Object.keys(inspectionMeta)) {
      const count = inspectionMeta[id];
      const inspection = await this.get(division, id);
      if (inspection) {
        await this.update(division, id, { ...inspection, issues: (inspection.issues || 0) + count });
      }
    }
  }
}
